import { useState } from "react";

import {
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import {
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { usePostHog } from "posthog-js/react";

import { formatCargoCoverage } from "components/CarrierInsuranceDocs/utils";
import {
  CarrierBlockedSource,
  ClientAppEvent,
  EVOTRUX_BORDER_GREY,
} from "constants/index";
import { useDisclosure, useRoleContext } from "hooks";
import { ISOStringToMonthDayYear } from "utils";

import { BlockCarrierModal } from "../../../BlockCarrierButton";

import type { CarrierInsurance } from "types/company";

interface Props {
  quote: ShipperCondensedQuote;
  insurance: CarrierInsurance | undefined;
  onViewHistory: VoidFunction;
}

interface InsuranceDetailRowProps {
  label: string;
  value: string;
  // Colours the whole row, label and value, when it needs attention.
  color?: string;
}

function InsuranceDetailRow(props: InsuranceDetailRowProps) {
  const { label, value, color } = props;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={3}
      sx={{ whiteSpace: "nowrap" }}
    >
      <Typography variant="body2" color={color ?? "text.secondary"}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }} color={color}>
        {value}
      </Typography>
    </Stack>
  );
}

interface InsuranceDetailsProps {
  insurance: CarrierInsurance;
}

function InsuranceDetails(props: InsuranceDetailsProps) {
  const { insurance } = props;
  // The nightly job marks a lapsed policy expired; the date check covers the
  // hours between the policy lapsing and that job running.
  const isExpired =
    insurance.marked_expired ||
    DateTime.fromISO(insurance.expires_on) < DateTime.now();

  return (
    <Stack spacing={0.25} sx={{ px: 2, pb: 1 }}>
      <InsuranceDetailRow
        label="Cargo Insurance"
        value={formatCargoCoverage({
          amountDollars: insurance.cargo_insurance_amount_dollars,
          currencyCode: insurance.cargo_insurance_currency,
        })}
      />
      <InsuranceDetailRow
        label={isExpired ? "Expired" : "Expires"}
        value={ISOStringToMonthDayYear(insurance.expires_on)}
        color={isExpired ? "error" : undefined}
      />
    </Stack>
  );
}

/**
 * The row's secondary actions, behind one control so the cluster keeps a single
 * primary button. Nothing here is time-critical: each is something a shipper
 * goes looking for rather than something the row is asking them to do.
 */
export function QuoteOverflowMenu(props: Props) {
  const { quote, insurance, onViewHistory } = props;
  const { role } = useRoleContext();
  const posthog = usePostHog();
  const analyticsProperties = {
    quote_id: quote.quote,
    shipment_id: quote.shipment,
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const close = () => setAnchorEl(null);
  const blockCarrierModal = useDisclosure(false);

  // Blocking is a shipper action. Only a shipper reaches this table today, so
  // this holds if the table is ever reused on a screen where that is not true.
  const canBlockCarrier = role?.type === "SHIPPER";

  return (
    <>
      <Tooltip title="More actions for this quote">
        <IconButton
          size="small"
          aria-label="More actions for this quote"
          onClick={(event) => {
            posthog?.capture(
              ClientAppEvent.SHIPPER_QUOTE_ACTIONS_MENU_OPENED,
              analyticsProperties,
            );
            setAnchorEl(event.currentTarget);
          }}
          // Squared off and outlined so it reads as a sibling of the button
          // beside it rather than a loose icon.
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: "4px",
            border: `1px solid ${EVOTRUX_BORDER_GREY}`,
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {/* Tooltips sit to the left so they never cover the menu itself. */}
        {insurance && (
          <Tooltip
            title="Open the carrier's insurance documents in a new tab"
            describeChild
            placement="left"
          >
            <MenuItem
              component="a"
              href={insurance.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                posthog?.capture(
                  ClientAppEvent.SHIPPER_QUOTE_INSURANCE_VIEWED,
                  analyticsProperties,
                );
                close();
              }}
              sx={{ gap: 0.75 }}
            >
              View Insurance
              <OpenInNewIcon
                titleAccess="Opens in new tab"
                fontSize="inherit"
                sx={{ color: "text.secondary" }}
              />
            </MenuItem>
          </Tooltip>
        )}
        {/* Read-only details, not a menu item. Separate from the item above
        because a Menu cannot take a Fragment as a child. */}
        {insurance && <InsuranceDetails insurance={insurance} />}
        <Tooltip
          title="See every offer and counter offer made on this quote"
          describeChild
          placement="left"
        >
          <MenuItem
            onClick={() => {
              posthog?.capture(
                ClientAppEvent.SHIPPER_QUOTE_HISTORY_VIEWED,
                analyticsProperties,
              );
              close();
              onViewHistory();
            }}
          >
            View History
          </MenuItem>
        </Tooltip>
        {canBlockCarrier && (
          <Tooltip
            title="Stop this carrier from seeing and quoting on your loads. You will be asked to confirm"
            describeChild
            placement="left"
          >
            <MenuItem
              onClick={() => {
                // The confirmation is tracked separately (CARRIER_BLOCKED), so
                // this counts the shippers who open it and back out.
                posthog?.capture(
                  ClientAppEvent.SHIPPER_QUOTE_BLOCK_CARRIER_CLICKED,
                  analyticsProperties,
                );
                close();
                blockCarrierModal.onOpen();
              }}
            >
              Block Carrier
            </MenuItem>
          </Tooltip>
        )}
      </Menu>
      {/* Outside the menu, so closing the menu does not unmount the dialog. */}
      {canBlockCarrier && (
        /*
        Every value here comes off the quote's author, the same role
        `carrier_id` names, so the person named in the confirmation is always
        the person the block lands on. `action_user_author` and `company`
        describe whoever took the latest action instead, which is the shipper
        from the moment they book, counter or decline.
        */
        <BlockCarrierModal
          open={blockCarrierModal.isOpen}
          onClose={blockCarrierModal.onClose}
          carrierId={quote.carrier_id}
          firstName={quote.created_by.first_name}
          lastName={quote.created_by.last_name}
          companyName={quote.created_by_company.name}
          source={CarrierBlockedSource.SHIPMENT_QUOTE_TABLE_ROW}
        />
      )}
    </>
  );
}
